import { c as _c } from "react/compiler-runtime";
import type { EditorView } from '@codemirror/view';
import { useCallback, useEffect, useRef } from 'react';
import type { TreeData } from '../../../../../types';
import { filterTopLevelASTElements, getTopLevelElements } from './utils/ast';
import { getPreviewElements } from './utils/preview';
export enum SCROLL_AREA {
  EDITOR = 'editor',
  PREVIEW = 'preview',
}
interface SyncScrollProps {
  previewRef: React.RefObject<HTMLDivElement | null>;
  editorViewRef: React.RefObject<EditorView | null>;
  treeDataRef: React.RefObject<TreeData | null>;
  currentScrollArea: SCROLL_AREA | null;
  editorContainerRef: React.RefObject<HTMLDivElement | null>;
}
export const useSyncScroll = t0 => {
  const $ = _c(37);
  const {
    previewRef,
    editorViewRef,
    treeDataRef,
    currentScrollArea,
    editorContainerRef
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const editorElementListRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const previewElementListRef = useRef(t2);
  const scrollTimeoutRef = useRef(null);
  const isSyncingRef = useRef(false);
  let t3;
  if ($[2] !== editorContainerRef || $[3] !== editorViewRef || $[4] !== previewRef || $[5] !== treeDataRef) {
    t3 = () => {
      if (!previewRef.current || !editorViewRef.current || !treeDataRef.current || !editorContainerRef.current) {
        return;
      }
      const previewElements = getPreviewElements(previewRef);
      editorElementListRef.current = [];
      previewElementListRef.current = [];
      const previewContainerTop = previewRef.current.offsetTop;
      const allASTElements = getTopLevelElements(treeDataRef.current.children);
      const filteredASTElements = filterTopLevelASTElements(allASTElements.filter(_temp));
      for (const [index, astElement] of filteredASTElements.entries()) {
        const view = editorViewRef.current;
        if (!view) {
          continue;
        }
        ;
        try {
          const line = view.state.doc.line(astElement.position.start.line);
          const offsetTop = view.lineBlockAt(line.from).top;
          editorElementListRef.current.push(offsetTop);
          if (previewElements[index]) {
            const previewElement = previewElements[index];
            const relativeOffsetTop = previewElement.offsetTop - previewContainerTop;
            previewElementListRef.current.push(relativeOffsetTop);
          }
        } catch (t4) {
          const error = t4;
          console.warn("Error mapping element:", error);
        }
      }
      if (previewElements.length > 0 && editorElementListRef.current.length > 0) {
        const lastPreviewElement = previewElements.at(-1);
        if (lastPreviewElement) {
          previewElementListRef.current.push(lastPreviewElement.offsetTop + lastPreviewElement.offsetHeight - previewContainerTop);
        }
        const view_0 = editorViewRef.current;
        if (view_0) {
          const lastLine = view_0.state.doc.line(view_0.state.doc.lines);
          const lastOffsetTop = view_0.lineBlockAt(lastLine.from).top + view_0.lineBlockAt(lastLine.from).height;
          editorElementListRef.current.push(lastOffsetTop);
        }
      }
    };
    $[2] = editorContainerRef;
    $[3] = editorViewRef;
    $[4] = previewRef;
    $[5] = treeDataRef;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const computePosition = t3;
  const findScrollElementIndex = _temp2;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (sourceScrollTop, sourceElementList, targetElementList, targetContainer) => {
      if (!targetContainer || isSyncingRef.current) {
        return;
      }
      const scrollElementIndex = findScrollElementIndex(sourceScrollTop, sourceElementList);
      if (scrollElementIndex !== null && scrollElementIndex >= 0) {
        const sourceSegmentStart = sourceElementList[scrollElementIndex];
        const sourceSegmentEnd = sourceElementList[scrollElementIndex + 1];
        const targetSegmentStart = targetElementList[scrollElementIndex];
        const targetSegmentEnd = targetElementList[scrollElementIndex + 1];
        if (sourceSegmentEnd > sourceSegmentStart) {
          const ratio = (sourceScrollTop - sourceSegmentStart) / (sourceSegmentEnd - sourceSegmentStart);
          const targetScrollTop = ratio * (targetSegmentEnd - targetSegmentStart) + targetSegmentStart;
          if (Math.abs(targetContainer.scrollTop - targetScrollTop) > 2) {
            isSyncingRef.current = true;
            targetContainer.scrollTop = targetScrollTop;
            isSyncingRef.current = false;
          }
        }
      }
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const syncScroll = t4;
  let t5;
  if ($[8] !== computePosition || $[9] !== currentScrollArea || $[10] !== editorContainerRef || $[11] !== previewRef) {
    t5 = (scrollType, scrollTop_0) => {
      if (currentScrollArea !== scrollType || isSyncingRef.current) {
        return;
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        if (isSyncingRef.current) {
          return;
        }
        computePosition();
        if (scrollType === SCROLL_AREA.EDITOR) {
          syncScroll(scrollTop_0, editorElementListRef.current, previewElementListRef.current, previewRef.current);
        } else {
          syncScroll(scrollTop_0, previewElementListRef.current, editorElementListRef.current, editorContainerRef.current);
        }
      }, 10);
    };
    $[8] = computePosition;
    $[9] = currentScrollArea;
    $[10] = editorContainerRef;
    $[11] = previewRef;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleScroll = t5;
  let t6;
  if ($[13] !== handleScroll) {
    t6 = scrollTop_1 => handleScroll(SCROLL_AREA.EDITOR, scrollTop_1);
    $[13] = handleScroll;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleEditorScroll = t6;
  let t7;
  if ($[15] !== handleScroll || $[16] !== previewRef) {
    t7 = () => {
      const preview = previewRef.current;
      if (!preview) {
        return;
      }
      handleScroll(SCROLL_AREA.PREVIEW, preview.scrollTop);
    };
    $[15] = handleScroll;
    $[16] = previewRef;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handlePreviewScroll = t7;
  let t8;
  if ($[18] !== editorContainerRef) {
    t8 = () => editorContainerRef.current?.scrollTop ?? 0;
    $[18] = editorContainerRef;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const getCurrentEditorScrollPosition = t8;
  let t9;
  if ($[20] !== previewRef) {
    t9 = () => previewRef.current?.scrollTop ?? 0;
    $[20] = previewRef;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const getCurrentPreviewScrollPosition = t9;
  let t10;
  if ($[22] !== computePosition || $[23] !== previewRef) {
    t10 = editorScrollTop => {
      computePosition();
      syncScroll(editorScrollTop, editorElementListRef.current, previewElementListRef.current, previewRef.current);
    };
    $[22] = computePosition;
    $[23] = previewRef;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const syncToPreview = t10;
  let t11;
  if ($[25] !== editorContainerRef) {
    t11 = previewScrollTop => {
      syncScroll(previewScrollTop, previewElementListRef.current, editorElementListRef.current, editorContainerRef.current);
    };
    $[25] = editorContainerRef;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const syncToEditor = t11;
  let t12;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
    t13 = [];
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  useEffect(t12, t13);
  let t14;
  if ($[29] !== computePosition || $[30] !== getCurrentEditorScrollPosition || $[31] !== getCurrentPreviewScrollPosition || $[32] !== handleEditorScroll || $[33] !== handlePreviewScroll || $[34] !== syncToEditor || $[35] !== syncToPreview) {
    t14 = {
      handleEditorScroll,
      handlePreviewScroll,
      computePosition,
      getCurrentEditorScrollPosition,
      getCurrentPreviewScrollPosition,
      syncToPreview,
      syncToEditor
    };
    $[29] = computePosition;
    $[30] = getCurrentEditorScrollPosition;
    $[31] = getCurrentPreviewScrollPosition;
    $[32] = handleEditorScroll;
    $[33] = handlePreviewScroll;
    $[34] = syncToEditor;
    $[35] = syncToPreview;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
};
export type { TreeData } from '../../../../../types';
function _temp(e) {
  return e.position?.start?.line;
}
function _temp2(scrollTop, elementList) {
  for (const [i, element] of elementList.entries()) {
    if (scrollTop < element) {
      return i - 1;
    }
  }
  return null;
}